import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, Building, User, Clock, Calendar, 
  Video, ExternalLink, Tag, History, MessageCircle,
  Award, TrendingUp, MapPin, AlertCircle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import CommentsSection from '../components/admin/CommentsSection';
import {
  formatCombinedTime,
  formatRelativeTime,
  formatAbsoluteTime,
  getMeetingStatus,
  getStatusBadgeConfig,
  formatDateSimple,
  formatDateTimeSimple
} from '../utils/timezone';
import ZoomIcon from '../components/icons/ZoomIcon';
import { ZoomLinkEntry } from '../components/scheduling';

export default function ConsultationDetail() {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [consultation, setConsultation] = useState(location.state?.consultation || null);
  const [loading, setLoading] = useState(!location.state?.consultation);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [updatingLabel, setUpdatingLabel] = useState(false);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Get current user info and token from localStorage
    const userInfo = localStorage.getItem('leadboard_user_info');
    const authToken = localStorage.getItem('leadboard_session_token');
    
    if (userInfo) {
      try {
        setCurrentUser(JSON.parse(userInfo));
      } catch (err) {
        console.error('Error parsing user info:', err);
      }
    }
    
    if (authToken) {
      setToken(authToken);
    }
  }, []);

  useEffect(() => {
    // If consultation data was passed via navigation state, use it
    if (location.state?.consultation) {
      setConsultation(location.state.consultation);
      setLoading(false);
    } else {
      // Fallback: fetch from API if accessed directly via URL
      fetchConsultationDetails();
    }
  }, [consultationId]);

  const fetchConsultationDetails = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('leadboard_session_token');
      const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${REACT_APP_BACKEND_URL}/api/v1/admin/consultations/${consultationId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You don\'t have permission to view this consultation');
        } else if (response.status === 404) {
          throw new Error('Consultation not found');
        } else {
          throw new Error('Failed to fetch consultation details');
        }
      }

      const data = await response.json();
      setConsultation(data);
    } catch (err) {
      console.error('Error fetching consultation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLabelChange = async (newLabel) => {
    if (!consultation || !token) return;

    try {
      setUpdatingLabel(true);
      const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(
        `${REACT_APP_BACKEND_URL}/api/v1/admin/consultations/${consultation.consultation_id}/label`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            label: newLabel === 'None' ? null : newLabel,
            notes: '' // Optional notes can be added later
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update label');
      }

      const data = await response.json();
      
      // Update local consultation state
      setConsultation(prev => ({
        ...prev,
        labels: {
          ...prev.labels,
          current: data.new_label
        }
      }));

      toast.success('Label updated successfully');
      
      // Optionally refresh consultation to get updated history
      fetchConsultationDetails();
    } catch (err) {
      console.error('Error updating label:', err);
      toast.error(err.message || 'Failed to update label');
    } finally {
      setUpdatingLabel(false);
    }
  };

  const getCustomerTypeBadgeColor = (type) => {
    const colors = {
      'new_client': 'bg-blue-600 text-white',
      'existing_client': 'bg-green-600 text-white',
      'partner': 'bg-purple-600 text-white',
      'other': 'bg-gray-600 text-white'
    };
    return colors[type] || 'bg-gray-600 text-white';
  };

  const getCustomerTypeLabel = (type) => {
    const labels = {
      'new_client': 'New Client',
      'existing_client': 'Existing Client',
      'partner': 'Partner',
      'other': 'Other'
    };
    return labels[type] || type;
  };

  const getConsultationStatusBadgeColor = (status) => {
    const colors = {
      'pending_scheduling': 'bg-yellow-500 text-white',
      'pending_approval': 'bg-orange-500 text-white',
      'approved': 'bg-green-500 text-white',
      'scheduled': 'bg-blue-500 text-white',
      'completed': 'bg-green-700 text-white',
      'no_show': 'bg-red-500 text-white',
      'cancelled': 'bg-gray-600 text-white',
      'denied': 'bg-red-700 text-white'
    };
    return colors[status] || 'bg-gray-500 text-white';
  };

  const getConsultationStatusLabel = (status) => {
    const labels = {
      'pending_scheduling': 'Pending Scheduling',
      'pending_approval': 'Pending Approval',
      'approved': 'Approved',
      'scheduled': 'Scheduled',
      'completed': 'Completed',
      'no_show': 'No Show',
      'cancelled': 'Cancelled',
      'denied': 'Denied'
    };
    return labels[status] || status;
  };

  const isHost = () => {
    if (!consultation?.assigned_to || !currentUser) return false;
    return consultation.assigned_to === currentUser._id;
  };

  const canEditLabel = () => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    return isHost();
  };

  const handleCancelConsultation = () => {
    if (window.confirm('Are you sure you want to cancel this consultation? This will notify the customer.')) {
      // Cancellation is now handled via the custom scheduling system
      toast.info('Please use the customer manage booking page to cancel.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center" data-testid="consultation-detail-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B2240] mx-auto"></div>
          <p className="mt-4 text-[#4C5A70]">Loading consultation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center" data-testid="consultation-detail-error">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-[#0B2240] mb-2">Error</h2>
              <p className="text-[#4C5A70] mb-4">{error}</p>
              <Button
                onClick={() => navigate(-1)}
                variant="outline"
                className="bg-[#0B2240] text-white hover:bg-[#0A1D35]"
                data-testid="error-back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!consultation) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <p className="text-[#4C5A70]">Consultation not found</p>
      </div>
    );
  }

  const meetingStatus = consultation.scheduled_time 
    ? getMeetingStatus(consultation.scheduled_time, 45)
    : 'scheduled';
  const statusBadge = getStatusBadgeConfig(meetingStatus);

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="consultation-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Back Button */}
        <div className="mb-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-[#0B2240] hover:bg-[#E9E6DA] mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Consultations
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-['Playfair_Display'] font-semibold text-[#0B2240]">
                Consultation Details
              </h1>
              <p className="text-[#4C5A70] mt-1">
                ID: {consultation.consultation_id}
              </p>
            </div>
            <Badge className={getConsultationStatusBadgeColor(consultation.status)} data-testid="consultation-status-badge">
              {getConsultationStatusLabel(consultation.status)}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information Card */}
            <Card className="bg-white shadow-sm" data-testid="customer-info-card">
              <CardHeader>
                <CardTitle className="flex items-center text-[#0B2240]">
                  <User className="h-5 w-5 mr-2 text-[#D4AF37]" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-[#4C5A70]">Name</p>
                    <p className="font-medium text-[#0B2240]" data-testid="customer-name">
                      {consultation.first_name} {consultation.last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#4C5A70]">Customer Type</p>
                    <Badge className={getCustomerTypeBadgeColor(consultation.customer_type)} data-testid="customer-type-badge">
                      {getCustomerTypeLabel(consultation.customer_type)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-[#4C5A70]">Email</p>
                    <a 
                      href={`mailto:${consultation.email}`}
                      className="font-medium text-[#1FA3A3] hover:underline flex items-center"
                      data-testid="customer-email"
                    >
                      <Mail className="h-4 w-4 mr-1" />
                      {consultation.email}
                    </a>
                  </div>
                  <div>
                    <p className="text-sm text-[#4C5A70]">Phone</p>
                    <a 
                      href={`tel:${consultation.phone}`}
                      className="font-medium text-[#1FA3A3] hover:underline flex items-center"
                      data-testid="customer-phone"
                    >
                      <Phone className="h-4 w-4 mr-1" />
                      {consultation.phone || 'N/A'}
                    </a>
                  </div>
                  {consultation.company && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-[#4C5A70]">Company</p>
                      <p className="font-medium text-[#0B2240] flex items-center" data-testid="customer-company">
                        <Building className="h-4 w-4 mr-1 text-[#4C5A70]" />
                        {consultation.company}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Consultation Details Card */}
            <Card className="bg-white shadow-sm" data-testid="consultation-details-card">
              <CardHeader>
                <CardTitle className="flex items-center text-[#0B2240]">
                  <MessageCircle className="h-5 w-5 mr-2 text-[#D4AF37]" />
                  Consultation Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {consultation.services && consultation.services.length > 0 && (
                  <div>
                    <p className="text-sm text-[#4C5A70] mb-2">Services Needed</p>
                    <div className="flex flex-wrap gap-2" data-testid="services-list">
                      {(consultation.services || []).map((service, index) => (
                        <Badge key={index} variant="outline" className="bg-[#F7F6F2] text-[#0B2240]">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {consultation.description && (
                  <div>
                    <p className="text-sm text-[#4C5A70] mb-2">Description</p>
                    <p className="text-[#0B2240] bg-[#F7F6F2] p-3 rounded-lg" data-testid="consultation-description">
                      {consultation.description}
                    </p>
                  </div>
                )}
                {consultation.revenue && (
                  <div>
                    <p className="text-sm text-[#4C5A70]">Revenue Range</p>
                    <p className="font-medium text-[#0B2240]" data-testid="revenue-range">
                      {consultation.revenue}
                    </p>
                  </div>
                )}
                {consultation.timeline && (
                  <div>
                    <p className="text-sm text-[#4C5A70]">Timeline</p>
                    <p className="font-medium text-[#0B2240]" data-testid="consultation-timeline">
                      {consultation.timeline}
                    </p>
                  </div>
                )}
                <Separator />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#4C5A70]">Created</p>
                    <p className="font-medium text-[#0B2240]" data-testid="created-date">
                      {formatDateTimeSimple(consultation.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#4C5A70]">Last Updated</p>
                    <p className="font-medium text-[#0B2240]" data-testid="updated-date">
                      {formatDateTimeSimple(consultation.updated_at)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Schedule Information Section */}
            <Card className="bg-white shadow-sm" data-testid="schedule-info-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-[#0B2240]">
                  <span className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Schedule Information
                  </span>
                  {consultation.scheduled_time && (
                    <Badge className={statusBadge.color + ' ' + statusBadge.textColor} data-testid="meeting-status-badge">
                      {statusBadge.icon} {statusBadge.label}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {consultation.scheduled_time ? (
                  <>
                    <div>
                      <p className="text-sm text-[#4C5A70] mb-2">Scheduled Time</p>
                      <div className="bg-[#F7F6F2] p-3 rounded-lg" data-testid="scheduled-time-display">
                        <p className="text-lg font-semibold text-[#0B2240]">
                          {formatCombinedTime(consultation.scheduled_time)}
                        </p>
                        <p className="text-sm text-[#4C5A70] mt-1">
                          Duration: 45 minutes
                        </p>
                      </div>
                    </div>
                    {/* Calendly removed - using custom scheduling system */}
                  </>
                ) : (
                  <div className="bg-[#FEF3C7] border border-[#F59E0B] p-4 rounded-lg" data-testid="not-scheduled-message">
                    <p className="text-sm text-[#92400E] font-medium flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Not Scheduled Yet
                    </p>
                    <p className="text-xs text-[#92400E] mt-1">
                      Customer will select a time slot to schedule their video consultation.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Zoom Meeting Information Section */}
            <Card className="bg-white shadow-sm" data-testid="zoom-meeting-info-card">
              <CardHeader>
                <CardTitle className="flex items-center text-[#0B2240]">
                  <ZoomIcon className="h-5 w-5 mr-2" />
                  Zoom Meeting Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {consultation.zoom_meeting_id ? (
                  <>
                    <div className="bg-[#F7F6F2] p-4 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#4C5A70]">Meeting ID</span>
                        <span className="font-mono text-[#0B2240] font-semibold" data-testid="zoom-meeting-id">
                          {consultation.zoom_meeting_id?.match(/.{1,3}/g)?.join(' ') || consultation.zoom_meeting_id}
                        </span>
                      </div>
                      {consultation.zoom_password && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#4C5A70]">Password</span>
                          <span className="font-mono text-[#0B2240] font-semibold" data-testid="zoom-password">
                            {consultation.zoom_password}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Join/Start Meeting Buttons */}
                    {consultation.zoom_join_url && consultation.status === 'scheduled' && (
                      <div className="space-y-3 pt-2">
                        {/* Host: Start Meeting Button */}
                        {isHost() && consultation.zoom_start_url && (
                          <Button
                            onClick={() => window.open(consultation.zoom_start_url, '_blank')}
                            className="w-full bg-gradient-to-b from-[#E8D89E] via-[#D4AF37] to-[#B99610] text-[#0B2240] hover:brightness-95 shadow-inner focus-visible:ring-2 focus-visible:ring-[#1FA3A3]"
                            data-testid="start-meeting-button"
                          >
                            <Video className="h-4 w-4 mr-2" />
                            Start Meeting as Host
                          </Button>
                        )}
                        
                        {/* All Users: Join Meeting Button */}
                        <Button
                          onClick={() => window.open(consultation.zoom_join_url, '_blank')}
                          className="w-full bg-[#1FA3A3] text-white hover:bg-[#157A7A] focus-visible:ring-2 focus-visible:ring-[#1FA3A3]"
                          data-testid="join-meeting-button"
                        >
                          <Video className="h-4 w-4 mr-2" />
                          Join Meeting
                        </Button>
                        
                        <div className="text-xs text-[#4C5A70] text-center">
                          Meeting starts {formatRelativeTime(consultation.scheduled_time)}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Show ZoomLinkEntry for admins to add Zoom link manually */
                  <ZoomLinkEntry
                    consultationId={consultation._id}
                    currentZoomUrl={consultation.zoom_join_url}
                    currentZoomPassword={consultation.zoom_password}
                    zoomEmailSentAt={consultation.zoom_email_sent_at}
                    zoomEmailSentCount={consultation.zoom_email_sent_count || 0}
                    status={consultation.status}
                    customerEmail={consultation.email}
                    customerName={`${consultation.first_name || ''} ${consultation.last_name || ''}`.trim()}
                    scheduledTime={consultation.scheduled_time ? formatDateTimeSimple(consultation.scheduled_time) : null}
                    onUpdate={(updates) => setConsultation(prev => ({ ...prev, ...updates }))}
                    sessionToken={token}
                  />
                )}
              </CardContent>
            </Card>

            {/* Consultation Management Actions - Using custom scheduling system */}
            {consultation.status === 'scheduled' && consultation.manage_token && (
              <Card className="bg-white shadow-sm" data-testid="consultation-actions-card">
                <CardHeader>
                  <CardTitle className="text-[#0B2240]">Manage Consultation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-[#4C5A70] mb-3">
                    Customer can manage their booking using the link sent to their email.
                  </p>
                  <Button
                    onClick={() => {
                      const manageUrl = `${window.location.origin}/book-consultation/manage/${consultation.manage_token}`;
                      navigator.clipboard.writeText(manageUrl);
                      toast.success('Manage booking link copied to clipboard');
                    }}
                    variant="outline"
                    className="w-full border-[#1FA3A3] text-[#1FA3A3] hover:bg-[#1FA3A3]/10"
                    data-testid="copy-manage-link-button"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Copy Manage Booking Link
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Comments Section */}
            <Card className="bg-white shadow-sm" data-testid="comments-section">
              <CardHeader>
                <CardTitle className="flex items-center text-[#0B2240]">
                  <MessageCircle className="h-5 w-5 mr-2 text-[#D4AF37]" />
                  Comments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {token && currentUser && (
                  <CommentsSection
                    entityType="consultation"
                    entityId={consultation.consultation_id}
                    token={token}
                    currentUser={currentUser}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Labels & Activity */}
          <div className="space-y-6">
            {/* Labels & Status Card */}
            <Card className="bg-white shadow-sm" data-testid="labels-status-card">
              <CardHeader>
                <CardTitle className="flex items-center text-[#0B2240]">
                  <Tag className="h-5 w-5 mr-2 text-[#D4AF37]" />
                  Labels & Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-[#4C5A70] mb-2">Current Label</p>
                  <Select
                    value={consultation.labels?.current || 'None'}
                    onValueChange={handleLabelChange}
                    disabled={!canEditLabel() || updatingLabel}
                    data-testid="label-dropdown"
                  >
                    <SelectTrigger data-testid="consultation-detail-selecttrigger-1" className="w-full">
                      <SelectValue placeholder="Select label" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem data-testid="consultation-detail-selectitem-5" value="None">None</SelectItem>
                      <SelectItem data-testid="consultation-detail-selectitem-4" value="New Potential Customer">New Potential Customer</SelectItem>
                      <SelectItem data-testid="consultation-detail-selectitem-3" value="Follow-up Needed">Follow-up Needed</SelectItem>
                      <SelectItem data-testid="consultation-detail-selectitem-2" value="Convert to Client">Convert to Client</SelectItem>
                      <SelectItem data-testid="consultation-detail-selectitem-1" value="Qualified Lead">Qualified Lead</SelectItem>
                    </SelectContent>
                  </Select>
                  {!canEditLabel() && (
                    <p className="text-xs text-[#4C5A70] mt-1">
                      Only admin and assigned representative can edit labels
                    </p>
                  )}
                </div>

                {consultation.labels?.history && consultation.labels.history.length > 0 && (
                  <div>
                    <Accordion type="single" collapsible>
                      <AccordionItem value="label-history" data-testid="label-history">
                        <AccordionTrigger data-testid="consultation-detail-accordiontrigger-1" className="text-sm text-[#0B2240]">
                          <span className="flex items-center">
                            <History className="h-4 w-4 mr-2" />
                            Label History ({consultation.labels.history.length})
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3">
                            {(consultation.labels.history || []).map((entry, index) => (
                              <div key={index} className="bg-[#F7F6F2] p-3 rounded-lg text-sm">
                                <p className="font-medium text-[#0B2240]">
                                  {entry.label || 'Removed label'}
                                </p>
                                <p className="text-xs text-[#4C5A70] mt-1">
                                  by {entry.set_by_name} • {formatDateTimeSimple(entry.set_at)}
                                </p>
                                {entry.notes && (
                                  <p className="text-xs text-[#0B2240] mt-1 italic">
                                    "{entry.notes}"
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}

                {consultation.lead_score && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm text-[#4C5A70] mb-2">Lead Score</p>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1">
                          <div className="h-2 bg-[#E9E6DA] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#1FA3A3]"
                              style={{ width: `${consultation.lead_score}%` }}
                              data-testid="lead-score-bar"
                            />
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-[#0B2240]" data-testid="lead-score-value">
                          {consultation.lead_score}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {consultation.lead_grade && (
                  <div>
                    <p className="text-sm text-[#4C5A70]">Lead Grade</p>
                    <Badge className="bg-green-600 text-white mt-1" data-testid="lead-grade-badge">
                      <Award className="h-3 w-3 mr-1" />
                      Grade {consultation.lead_grade}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Info Card */}
            <Card className="bg-white shadow-sm" data-testid="quick-info-card">
              <CardHeader>
                <CardTitle className="flex items-center text-[#0B2240]">
                  <TrendingUp className="h-5 w-5 mr-2 text-[#D4AF37]" />
                  Quick Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {consultation.source && (
                  <div className="flex justify-between">
                    <span className="text-[#4C5A70]">Source</span>
                    <span className="font-medium text-[#0B2240]" data-testid="consultation-source">
                      {consultation.source.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}
                {consultation.linked_lead_id && (
                  <div className="flex justify-between">
                    <span className="text-[#4C5A70]">Linked Lead</span>
                    <span className="font-medium text-[#1FA3A3] hover:underline cursor-pointer" data-testid="linked-lead-id">
                      {consultation.linked_lead_id}
                    </span>
                  </div>
                )}
                {consultation.ip_address && (
                  <div className="flex justify-between">
                    <span className="text-[#4C5A70]">IP Address</span>
                    <span className="font-mono text-xs text-[#0B2240]" data-testid="ip-address">
                      {consultation.ip_address}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
